import { z } from 'zod';
import { SlugSchema, TimestampSchema, UuidSchema } from './primitives';

/**
 * Tenant status enum — mirrors the DB `tenant_status` enum created in
 * migration 0001 (see SAZEO_V2_ANATOMY.md §5.1).
 */
export const TenantStatusSchema = z.enum(['active', 'suspended', 'archived']);
export type TenantStatus = z.infer<typeof TenantStatusSchema>;

/**
 * Plan slug (not a UUID) — matches `tenants.plan_id text default 'free'`
 * in migration 0001. Billing tables arrive in a later migration and will
 * FK-link on this slug (e.g., `billing_plans.slug`).
 */
export const PlanIdSchema = z.string().min(1).max(64);
export type PlanId = z.infer<typeof PlanIdSchema>;

/** Shape of a row from the `tenants` table. */
export const TenantSchema = z.object({
  id: UuidSchema,
  slug: SlugSchema,
  name: z.string().min(1).max(120),
  logo_url: z.string().url().nullable(),
  status: TenantStatusSchema,
  plan_id: PlanIdSchema,
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type Tenant = z.infer<typeof TenantSchema>;

/** Submitted at signup / workspace creation. */
export const CreateTenantInputSchema = z.object({
  slug: SlugSchema,
  name: z.string().min(1).max(120),
  logo_url: z.string().url().optional(),
});
export type CreateTenantInput = z.infer<typeof CreateTenantInputSchema>;

/** Partial update payload — only mutable fields are exposed. */
export const UpdateTenantInputSchema = z
  .object({
    name: z.string().min(1).max(120),
    logo_url: z.string().url().nullable(),
    status: TenantStatusSchema,
  })
  .partial();
export type UpdateTenantInput = z.infer<typeof UpdateTenantInputSchema>;
